<template>
  <!--
    Kombinierte Kopfleisten-Schaltfläche (Issue: 3er-Kombination):
    Glocke + Sprache in einer Reihe, darunter das aktuelle
    KI-Dokumenten-Kontingent als Balken (X von 150 Seiten).
    Fasst die zuvor einzeln stehenden Bedienelemente zu einem
    zusammenhängenden Steuerelement zusammen.
  -->
  <div class="hux-cluster">
    <div class="hux-cluster__row">
      <NotificationBell class="hux-cluster__bell" />
      <span class="hux-cluster__divider" aria-hidden="true" />
      <LanguageSwitcher class="hux-cluster__lang" location="bottom" compact />
    </div>

    <button
      v-if="overview"
      type="button"
      class="hux-quota"
      :class="stateClass"
      :aria-label="quotaTooltip"
      @click="goToSubscription"
    >
      <Sparkles :size="12" class="hux-quota__icon" />
      <span class="hux-quota__bar">
        <span
          class="hux-quota__bar-fill"
          :class="stateClass"
          :style="{ width: usedPercent + '%' }"
        />
      </span>
      <span class="hux-quota__label">
        {{ overview.totalUsed }} <span class="hux-quota__label-sep">/</span> {{ overview.totalIncluded }}
      </span>
      <!--
        Kundenfeedback 29.08.26, Punkt II: Beim Hover muss erklärt werden,
        was der Zähler bedeutet (KI-Seiten-Kontingent) – der native
        title-Tooltip war zu spärlich und erschien verzögert.
      -->
      <v-tooltip activator="parent" location="bottom" max-width="300" content-class="hux-quota-tip">
        <div class="hux-quota-tip__head">{{ $gt('KI-Seiten-Kontingent') }}</div>
        <div>{{ quotaTooltip }}</div>
        <div class="hux-quota-tip__hint">
          {{ $gt('Jede Seite, die die KI-Belegerkennung liest (z. B. beim Hochladen von Gutachten oder Rechnungen), zählt als eine KI-Seite. Das Kontingent gilt pro Standort und wird zu Monatsbeginn zurückgesetzt. Klicken für Details und Zubuchung.') }}
        </div>
      </v-tooltip>
    </button>
  </div>
</template>

<script>
import { Sparkles } from "lucide-vue-next";
import NotificationBell from "@/components/app/NotificationBell.vue";
import LanguageSwitcher from "@/components/app/LanguageSwitcher.vue";
import { getAIPageOverview } from "@/services/aiPagesApi";

const POLL_MS = 60_000;

export default {
  name: "HeaderUtilityCluster",
  components: { Sparkles, NotificationBell, LanguageSwitcher },
  data() {
    return {
      overview: null,
      pollTimer: null,
    };
  },
  computed: {
    stateClass() {
      const o = this.overview;
      if (!o) return "hux--ok";
      if (o.totalAvailable <= 0) return "hux--exhausted";
      if (o.usagePercent >= 80) return "hux--warn";
      return "hux--ok";
    },
    usedPercent() {
      const o = this.overview;
      if (!o || o.totalIncluded <= 0) return 0;
      return Math.min(100, Math.max(0, (o.totalUsed / o.totalIncluded) * 100));
    },
    quotaTooltip() {
      const o = this.overview;
      if (!o) return "";
      const base = `${o.totalUsed} von ${o.totalIncluded} KI-Seiten genutzt`;
      const rest = `${o.totalAvailable} verfügbar`;
      return `${base} · ${rest}`;
    },
  },
  mounted() {
    this.fetchOverview();
    this.pollTimer = setInterval(() => this.fetchOverview(), POLL_MS);
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    async fetchOverview() {
      try {
        this.overview = await getAIPageOverview();
      } catch {
        // Nicht kritisch für die Navigation – Balken bleibt still ausgeblendet.
      }
    },
    goToSubscription() {
      // KI-Kontingente werden auf der Rechnungsseite verwaltet (das frühere
      // /abonnement wurde entfernt – Kundenfeedback 30.08.26).
      this.$router.push({ name: "PageInvoices", query: { modal: "aipages" } });
    },
  },
};
</script>

<style scoped>
.hux-cluster {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-right: 12px;
  padding: 3px 6px 5px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.hux-cluster__row {
  display: flex;
  align-items: center;
  gap: 2px;
}

.hux-cluster__divider {
  width: 1px;
  height: 22px;
  background: rgba(255, 255, 255, 0.14);
  margin: 0 2px;
}

/* Eingebettete Bell/Language-Komponenten an den Cluster angleichen. */
.hux-cluster__bell :deep(.notification-bell) {
  margin-right: 0;
}
.hux-cluster__lang :deep(.lang-switch) {
  background: transparent;
  border-color: transparent;
  color: #fff;
  padding: 6px 8px;
  backdrop-filter: none;
}
.hux-cluster__lang :deep(.lang-switch:hover) {
  background: rgba(255, 255, 255, 0.1);
}
.hux-cluster__lang :deep(.lang-switch__chevron) {
  color: rgba(255, 255, 255, 0.7);
}

/* KI-Kontingent-Balken */
.hux-quota {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  padding: 3px 6px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s;
}
.hux-quota:hover {
  background: rgba(255, 255, 255, 0.08);
}

.hux-quota__icon {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.75);
}
.hux-quota.hux--warn .hux-quota__icon {
  color: #fbbf24;
}
.hux-quota.hux--exhausted .hux-quota__icon {
  color: #f87171;
}

.hux-quota__bar {
  flex: 1 1 auto;
  min-width: 56px;
  height: 5px;
  background: rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  overflow: hidden;
}
.hux-quota__bar-fill {
  display: block;
  height: 100%;
  background: #34d399;
  border-radius: 999px;
  transition: width 0.24s ease;
}
.hux-quota__bar-fill.hux--warn {
  background: #fbbf24;
}
.hux-quota__bar-fill.hux--exhausted {
  background: #f87171;
}

.hux-quota__label {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.hux-quota__label-sep {
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

/* Erklärungs-Tooltip zum KI-Kontingent (global, da v-tooltip teleportiert). */
:global(.hux-quota-tip) {
  font-size: 12px;
  line-height: 1.45;
}
:global(.hux-quota-tip .hux-quota-tip__head) {
  font-weight: 700;
  margin-bottom: 2px;
}
:global(.hux-quota-tip .hux-quota-tip__hint) {
  margin-top: 6px;
  opacity: 0.85;
}
</style>
